import round from 'lodash/round';

import {
  GPUCountUnit,
  NodeComponent,
  NodeComponentUnit,
  NodeExtension,
} from '@cast/types';

import { NodeWithDependencies } from '../types';

const ROUND_PRECISION = 2;

const convertGpuCount = ({
  count,
  countUnit,
}: {
  count: number;
  countUnit?: GPUCountUnit | NodeComponentUnit;
}): number => {
  switch (countUnit) {
    case 'COUNT':
      // 1 unit = 1 GPU
      return count;
    case 'MILLI_COUNT':
      // 1000 units = 1 GPU
      return count / 1000;
    default:
      // GPU count could only be in COUNT or MILLI_COUNT
      return count;
  }
};

const getGpuCountFromComponent = (gpuComponent: NodeComponent): number => {
  if (!gpuComponent?.amount) {
    return 0;
  }
  return round(
    convertGpuCount({
      count: gpuComponent.amount,
      countUnit: gpuComponent.unit,
    }),
    ROUND_PRECISION
  );
};

const getGpuCountFromExtension = (gpuExtension: NodeExtension): number => {
  if (!gpuExtension?.gpu?.count) {
    return 0;
  }

  return round(
    convertGpuCount({
      count: gpuExtension.gpu.count,
      countUnit: gpuExtension.gpu.countUnit,
    }),
    ROUND_PRECISION
  );
};

export const getGpuCount = (node?: NodeWithDependencies): number => {
  // There should be only one GPU component. If there are multiple, they are aggregated on the backend side.
  const gpuComponent = node?.pricingInfo?.components?.find(
    (component) => component.type === 'GPU'
  );

  /*
   * Prioritize components when checking for GPUs, and fall back to extensions if none are found.
   *
   * Rationale:
   * - Components hold integral (non-detachable) GPUs.
   * - Extensions hold optional/extra GPUs (attachable in GCP).
   * - A node with an integral GPU cannot have an attachable one.
   *
   * Note:
   * - Checking components first ensures correct GPU counts while the backend
   *   removes duplication in extensions. Checking extensions first could lead
   *   to incorrect counts during the transition.
   */
  if (gpuComponent) {
    return getGpuCountFromComponent(gpuComponent);
  }

  // There should be only one GPU extension. If there are multiple, they are aggregated on the backend side.
  const gpuExtension = node?.pricingInfo?.extensions?.find(
    (extension) => extension.type === 'GPU'
  );

  if (gpuExtension) {
    return getGpuCountFromExtension(gpuExtension);
  }

  return 0;
};
